'use client'

import * as React from 'react'
import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { Slot } from '@radix-ui/react-slot'
import { Loader } from 'lucide-react'
import { motion } from 'motion/react'

import type { VariantProps } from '@config/tailwind/cva'
import cn from '@config/tailwind/cn'
import { cva } from '@config/tailwind/cva'

import { typographyVariants } from '../lib'

const buttonVariants = cva(
  'ui:inline-flex ui:items-center ui:justify-center ui:rounded-md ui:transition-colors ui:focus-visible:outline-none ui:focus-visible:ring-2 ui:focus-visible:ring-ring ui:focus-visible:ring-offset-2 ui:disabled:opacity-50 ui:disabled:pointer-events-none ui:ring-offset-neutral-white ui:cursor-pointer ui:select-none',
  {
    variants: {
      variant: {
        primary: 'ui:bg-blue-100 ui:text-neutral-white ui:hover:opacity-90 ui:rounded-full',
        gradient: 'ui:bg-gradient-orange-red ui:text-neutral-white ui:hover:opacity-90 ui:rounded-full',
        destructive:
          'ui:bg-error ui:text-neutral-white ui:hover:opacity-90 ui:focus-visible:ring-error ui:rounded-full',
        outline: 'ui:border ui:border-blue-70 ui:text-neutral-black ui:hover:opacity-90 ui:rounded-full',
        secondary: 'ui:bg-blue-10 ui:text-blue-100 ui:hover:opacity-90 ui:rounded-full',
        ghost: 'ui:text-blue-100 ui:hover:opacity-90 ui:rounded-none',
        link: 'ui:underline-offset-4 ui:hover:underline ui:text-neutral-black ui:rounded-none',
        none: '',
      },
      size: {
        default: 'ui:h-10 ui:py-2 ui:px-4',
        xs: 'ui:h-6 ui:px-2',
        sm: 'ui:h-8 ui:px-3',
        lg: 'ui:h-14 ui:px-8',
        xl: 'ui:h-24 ui:px-12',
        icon: 'ui:h-10 ui:w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
)

const loaderVariants = cva('ui:mr-2 ui:animate-spin', {
  variants: {
    size: {
      default: 'ui:h-5 ui:w-5',
      xs: 'ui:h-3 ui:w-3',
      sm: 'ui:h-4 ui:w-4',
      lg: 'ui:h-5 ui:w-5',
      xl: 'ui:h-6 ui:w-6',
      icon: 'ui:h-0 ui:w-0',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const sizeTypography = {
  default: 'label-md',
  xs: 'label-sm',
  sm: 'label-md',
  lg: 'label-lg',
  xl: 'headline-sm',
  icon: 'label-md',
} as const satisfies Record<
  NonNullable<VariantProps<typeof buttonVariants>['size']>,
  NonNullable<Parameters<typeof typographyVariants>[0]>['variant']
>

export interface ButtonProps
  extends VariantProps<typeof buttonVariants>, Omit<ButtonPrimitive.Props, 'className' | 'render'> {
  className?: string
  /**
   * Will display the loading spinner and disable the button.
   */
  loading?: boolean
  /**
   * When true, the button will render as the child element (e.g., anchor tag).
   * Motion animations will still be applied.
   */
  asChild?: boolean
  /**
   * When true, renders a simple button without motion animations.
   * Loading and other features are still supported.
   */
  baseButton?: boolean
  children?: React.ReactNode
}

const MotionSlot = motion.create(Slot)

// Type that excludes event handlers that conflict with Framer Motion
type MotionSafeProps<T> = Omit<
  T,
  'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration' | 'onDragStart' | 'onDrag' | 'onDragEnd'
>

function Button({
  className,
  variant = 'primary',
  size = 'default',
  loading,
  children,
  disabled,
  asChild = false,
  baseButton = false,
  ...props
}: ButtonProps) {
  const isLoading = loading === true
  const isDisabled = disabled ?? isLoading
  const resolvedSize = size ?? 'default'
  const buttonClass = cn(
    typographyVariants({ variant: sizeTypography[resolvedSize] }),
    buttonVariants({ variant, size, className }),
  )

  // If baseButton is true, render without motion animations
  if (baseButton) {
    return (
      <ButtonPrimitive type='button' data-slot='button' disabled={isDisabled} className={buttonClass} {...props}>
        {isLoading && <Loader className={cn('ui:mr-2 ui:animate-spin', loaderVariants({ size }))} />}
        {children}
      </ButtonPrimitive>
    )
  }

  // Exclude event handlers that conflict with Framer Motion
  const {
    onAnimationStart: _onAnimationStart,
    onAnimationEnd: _onAnimationEnd,
    onAnimationIteration: _onAnimationIteration,
    onDragStart: _onDragStart,
    onDrag: _onDrag,
    onDragEnd: _onDragEnd,
    ...motionProps
  } = props
  const safeMotionProps: MotionSafeProps<typeof props> = motionProps

  if (asChild) {
    return (
      <MotionSlot
        data-slot='button'
        className={buttonClass}
        whileHover={isDisabled ? undefined : { scale: 1.05 }}
        whileTap={isDisabled ? undefined : { scale: 0.95 }}
        {...safeMotionProps}
      >
        {children}
      </MotionSlot>
    )
  }

  return (
    <ButtonPrimitive
      type='button'
      data-slot='button'
      disabled={isDisabled}
      className={buttonClass}
      render={(renderProps) => {
        // Exclude event handlers from renderProps as well
        const {
          onAnimationStart: _onAnimationStart2,
          onAnimationEnd: _onAnimationEnd2,
          onAnimationIteration: _onAnimationIteration2,
          onDragStart: _onDragStart2,
          onDrag: _onDrag2,
          onDragEnd: _onDragEnd2,
          ...motionRenderProps
        } = renderProps
        const safeRenderProps: MotionSafeProps<typeof renderProps> = motionRenderProps

        return (
          <motion.button
            {...safeRenderProps}
            whileHover={isDisabled ? undefined : { scale: 1.025 }}
            whileTap={isDisabled ? undefined : { scale: 0.975 }}
          />
        )
      }}
      {...props}
    >
      {isLoading && <Loader className={cn('ui:mr-2 ui:animate-spin', loaderVariants({ size }))} />}
      {children}
    </ButtonPrimitive>
  )
}

type ButtonVariant = VariantProps<typeof buttonVariants>['variant']

export { Button, buttonVariants, type ButtonVariant }
